import { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAppContext } from '../../../contexts/AppProvider';
import Select from 'react-select';

const API_URL = process.env.REACT_APP_API_URL;

interface ProjectType {
    id?: number;
    title: string;
    description: string;
    banner_image?: string | File | null;
    skills: number[];
    skill_data?: SkillOption[];
}

interface SkillOption {
    value: number;
    label: string;
}

const Project = () => {
    const { authToken } = useAppContext();
    const [project, setProject] = useState<ProjectType[]>([]);
    const [isEdit, setIsEdit] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [skills, setSkills] = useState<SkillOption[]>([]);
    const [selectedSkills, setSelectedSkills] = useState<SkillOption[]>([]);
    const [formData, setFormData] = useState<ProjectType>({
        id: undefined,
        title: '',
        description: '',
        banner_image: null,
        skills: [],
        skill_data: []
    });

    const resetForm = () => {
        setFormData({
            id: undefined,
            title: '',
            description: '',
            banner_image: null,
            skills: [],
            skill_data: []
        });
        setSelectedFile(null);
        setIsEdit(false);
    };

    useEffect(() => {
        if (authToken) {
            fetchAllProject();
            fetchSkills();
        }
    }, [authToken]);

    const fetchSkills = async () => {
        try {
            const response = await axios.get(`${API_URL}/skill`, {
                headers: {
                    Authorization: `Bearer ${authToken}`
                }
            });
            const skillOptions = response.data.message.map((skill: any) => ({
                value: skill.id,
                label: skill.title
            }));
            setSkills(skillOptions);
        } catch (error) {
            console.error('Error fetching skills:', error);
            toast.error('Gagal mengambil data skill');
        }
    };

    const fetchAllProject = async () => {
        try {
            const response = await axios.get(`${API_URL}/project`, {
                headers: {
                    Authorization: `Bearer ${authToken}`
                }
            });
            console.log('API Response:', response.data);
            if (response.data && response.data.data && Array.isArray(response.data.data)) {
                const projectsWithSkills = response.data.data.map((proj: any) => ({
                    ...proj,
                    skill_data: proj.skills ? proj.skills.map((skill: any) => ({
                        value: skill.id,
                        label: skill.title
                    })) : []
                }));
                setProject(projectsWithSkills);
            } else {
                console.error('Invalid project data format:', response.data);
                toast.error('Format data project tidak valid');
            }
        } catch (error) {
            console.error('Error fetching projects:', error);
            toast.error('Gagal mengambil data project');
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        // Validasi form
        if (!formData.title.trim()) {
            toast.error('Judul project harus diisi');
            return;
        }
        if (!formData.description.trim()) {
            toast.error('Deskripsi project harus diisi');
            return;
        }
        if (!isEdit && !selectedFile) {
            toast.error('Banner image harus dipilih');
            return;
        }
        if (!selectedSkills.length) {
            toast.error('Minimal satu skill harus dipilih');
            return;
        }

        const formDataToSend = new FormData();
        formDataToSend.append('title', formData.title.trim());
        formDataToSend.append('description', formData.description.trim());
        if (selectedFile) {
            formDataToSend.append('banner_image', selectedFile);
        } else if (isEdit && formData.banner_image && typeof formData.banner_image === 'string') {
            // Jika dalam mode edit dan tidak ada file baru dipilih, gunakan banner_image yang ada
            formDataToSend.append('banner_image', formData.banner_image);
        }
        
        // Mengirim skills sebagai array of IDs
        const skillIds = selectedSkills.map(skill => skill.value);
        skillIds.forEach((skillId, index) => {
            formDataToSend.append(`skills[${index}]`, skillId.toString());
        });

        try {
            if (isEdit && formData.id) {
                formDataToSend.append('_method', 'PUT');
                await axios.post(`${API_URL}/project/${formData.id}`, formDataToSend, {
                    headers: {
                        Authorization: `Bearer ${authToken}`,
                        'Content-Type': 'multipart/form-data'
                    }
                });

                toast.success('Project berhasil diperbarui!', {
                    duration: 3000
                });
            } else {
                await axios.post(`${API_URL}/project`, formDataToSend, {
                    headers: {
                        Authorization: `Bearer ${authToken}`,
                        'Content-Type': 'multipart/form-data'
                    }
                });

                toast.success('Project baru berhasil ditambahkan!', {
                    duration: 3000
                });
            }

            resetForm();
            setSelectedSkills([]);
            fetchAllProject();
        } catch (error) {
            console.error('Error:', error);
            toast.error('Terjadi kesalahan saat memproses project', {
                duration: 3000
            });
        }
    };

    const handleEdit = (singleProject: ProjectType) => {
        setIsEdit(true);
        const projectSkills = singleProject.skill_data || [];
        setSelectedSkills(projectSkills);
        setFormData({
            id: singleProject.id,
            title: singleProject.title,
            description: singleProject.description,
            banner_image: singleProject.banner_image,
            skills: projectSkills.map(skill => skill.value),
            skill_data: projectSkills
        });
        setSelectedFile(null);
    };

    const handleDelete = (singleProject: ProjectType) => {
        if (!singleProject.id) return;

        const confirmDelete = window.confirm('Apakah Anda yakin ingin menghapus project ini?');
        if (!confirmDelete) return;

        const deleteProject = async () => {
            try {
                const response = await axios.delete(`${API_URL}/project/${singleProject.id}`, {
                    headers: {
                        Authorization: `Bearer ${authToken}`
                    }
                });

                toast.success('Project berhasil dihapus!', {
                    duration: 3000
                });

                fetchAllProject();
            } catch (error) {
                console.error('Error:', error);
                toast.error('Gagal menghapus project', {
                    duration: 3000
                });
            }
        };

        deleteProject();
    };

    return (
        <div className="grid md:grid-cols-2 gap-8 p-6 flex-col min-h-screen">

            <div className="bg-white p-8 rounded-xl shadow-lg transition-all duration-300 hover:shadow-xl">
                <h2 className="text-3xl font-bold text-gray-800 mb-8">{isEdit ? 'Edit' : 'Add'} Project</h2>
                <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Title</label>
                        <input
                            type="text"
                            value={formData.title}
                            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                            placeholder="Enter project title"
                            required
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Description</label>
                        <textarea
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                            rows={4}
                            placeholder="Enter project description"
                            required
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Banner Image</label>
                        <input
                            type="file"
                            onChange={(e) => setSelectedFile(e.target.files ? e.target.files[0] : null)}
                            className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                            accept="image/*"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Skills</label>
                        <Select
                            isMulti
                            value={selectedSkills}
                            onChange={(selected) => {
                                const selectedArray = Array.isArray(selected) ? selected : [];
                                setSelectedSkills(selectedArray);
                                setFormData({
                                    ...formData,
                                    skills: selectedArray.map(option => option.value)
                                });
                            }}
                            options={skills}
                            className="mt-1"
                            placeholder="Select skills"
                        />
                    </div>
                    <div className="flex justify-end space-x-4">
                        {isEdit && (
                            <button
                                type="button"
                                onClick={resetForm}
                                className="px-6 py-3 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                            >
                                Cancel
                            </button>
                        )}
                        <button
                            type="submit"
                            className="px-6 py-3 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                            {isEdit ? 'Update' : 'Add'} Project
                        </button>
                    </div>
                </form>
            </div>

            <div className="bg-white rounded-lg shadow-lg p-6">
                <h2 className="text-2xl font-bold text-gray-800 mb-6">Projects List</h2>
                <div className="overflow-x-auto">
                    {Array.isArray(project) && project.length > 0 ? (
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">#</th>
                                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Banner</th>
                                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Title</th>
                                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Description</th>
                                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Skills</th>
                                    <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {project.map((singleProject, index) => (
                                    <tr key={index} className="hover:bg-gray-50 transition-colors duration-150">
                                        <td className="px-4 py-3 text-sm text-gray-600">{index + 1}</td>
                                        <td className="px-4 py-3">
                                            {singleProject.banner_image && typeof singleProject.banner_image === "string" ? (
                                                <img
                                                    src={singleProject.banner_image}
                                                    alt="Banner"
                                                    className="w-12 h-12 rounded-md object-cover border border-gray-200"
                                                />
                                            ) : (
                                                <div className="w-12 h-12 rounded-md bg-gray-200 flex items-center justify-center text-gray-500 text-xs">
                                                    No Image
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{singleProject.title}</td>
                                        <td className="px-4 py-3 text-sm text-gray-600 truncate max-w-xs">{singleProject.description}</td>
                                        <td className="px-4 py-3 text-sm text-gray-600">
                                            {singleProject.skill_data && singleProject.skill_data.length > 0 ? (
                                                <div className="flex flex-wrap gap-1">
                                                    {singleProject.skill_data.map((skill, i) => (
                                                        <span key={i} className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                                                            {skill.label}
                                                        </span>
                                                    ))}
                                                </div>
                                            ) : (
                                                <span className="text-gray-400 text-xs">No Skills</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-right space-x-2">
                                            <button
                                                onClick={() => handleEdit(singleProject)}
                                                className="px-3 py-1 bg-yellow-500 text-white rounded-md hover:bg-yellow-600 text-sm transition"
                                            >
                                                Edit
                                            </button>
                                            <button
                                                onClick={() => handleDelete(singleProject)}
                                                className="px-3 py-1 bg-red-500 text-white rounded-md hover:bg-red-600 text-sm transition"
                                            >
                                                Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <div className="text-center py-8 text-gray-500">
                            No projects found. Add your first project!
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Project;